import { getApiBaseUrl } from '../../../utils/apiConfig';
import { BOARD_ORDERS_LIST_VIEW, withBoardOrdersListView } from '../utils/boardOrdersListView';
import { assembleBoardTeamData } from '../utils/assembleBoardTeamData';

/**
 * GET /api/board/bootstrap — um pacote para o boot do Board.
 * Fallback: monta o mesmo shape com as rotas legadas (processo intacto).
 */

function apiBase() {
  return getApiBaseUrl();
}

function authHeaders(token) {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

export function buildBoardBootstrapUrl({ techIds = [], view = BOARD_ORDERS_LIST_VIEW } = {}) {
  const params = new URLSearchParams();
  params.set('view', view === 'full' ? 'full' : 'card');
  const ids = [...new Set((techIds || []).map((id) => String(id ?? '').trim()).filter(Boolean))];
  if (ids.length) params.set('techIds', ids.join(','));
  if (!ids.length) params.set('includeTechnicians', '0');
  return `${apiBase()}/api/board/bootstrap?${params.toString()}`;
}

async function fetchJson(url, options = {}) {
  const res = await fetch(url, options);
  const json = await res.json().catch(() => null);
  return { res, json };
}

/** Fallback legado — mesmas rotas que o Board já usava. */
export async function fetchBoardBootstrapLegacy({ token, techIds = [] } = {}) {
  const headers = authHeaders(token);
  const ids = [...new Set((techIds || []).map((id) => String(id ?? '').trim()).filter(Boolean))];

  const openP = fetchJson(withBoardOrdersListView(`${apiBase()}/api/orders/open`), { headers });
  const teamP = fetchJson(`${apiBase()}/api/team/board-bootstrap`, { headers });
  const retornoP = fetchJson(`${apiBase()}/api/config/retorno`, { headers });
  const autoP = fetchJson(`${apiBase()}/api/config/automation-retorno`, { headers });
  const bellP = fetchJson(`${apiBase()}/api/orders/futura-retorno-condt-summary`, { headers });

  const countsP = ids.length
    ? fetchJson(
        `${apiBase()}/api/orders/technicians/counts?ids=${ids.map(encodeURIComponent).join(',')}`,
        { headers }
      )
    : Promise.resolve({ res: { ok: true, status: 200 }, json: { success: true, data: null } });

  const techP = ids.length
    ? fetchJson(`${apiBase()}/api/orders/technicians/batch`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          technicianIds: ids,
          view: BOARD_ORDERS_LIST_VIEW,
          boardRefresh: 'manual',
        }),
      })
    : Promise.resolve({ res: { ok: true, status: 200 }, json: null });

  const [openR, teamR, retornoR, autoR, bellR, countsR, techR] = await Promise.all([
    openP,
    teamP,
    retornoP,
    autoP,
    bellP,
    countsP,
    techP,
  ]);

  if (!openR.res.ok || !openR.json?.success) {
    const err = new Error(openR.json?.message || `open_http_${openR.res.status}`);
    err.status = openR.res.status;
    throw err;
  }

  return {
    success: true,
    source: 'legacy',
    data: {
      open: openR.json,
      team: teamR.json?.success ? teamR.json.data : null,
      technicians: techR.json?.success ? techR.json : null,
      counts: countsR.json?.success ? countsR.json.data : null,
      configs: {
        retorno: retornoR.json?.success ? retornoR.json.data : { codstatus: '', extraStatuses: [] },
        automationRetorno: autoR.json?.success
          ? autoR.json.data
          : { statusPedidoDisponivel: '', statusOsEncaminhar: '' },
      },
      bell: bellR.json?.success ? bellR.json.data : null,
      meta: { techIds: ids, view: 'card', legacy: true },
    },
  };
}

/**
 * @returns {Promise<{ success: boolean, source: string, data: object, status?: number }>}
 */
export async function fetchBoardBootstrap({ token, techIds = [], view = BOARD_ORDERS_LIST_VIEW } = {}) {
  const headers = authHeaders(token);
  try {
    const url = buildBoardBootstrapUrl({ techIds, view });
    const { res, json } = await fetchJson(url, { headers });
    if (res.status === 401) {
      const err = new Error(json?.message || 'Sessão expirada');
      err.status = 401;
      throw err;
    }
    if (res.ok && json?.success && json.data?.open?.success) {
      return { success: true, source: 'bootstrap', data: json.data, status: res.status };
    }
  } catch (e) {
    if (e?.status === 401) throw e;
    /* cai no legado */
  }
  return fetchBoardBootstrapLegacy({ token, techIds });
}

export function applyBoardBootstrapConfigs(data, setters) {
  const retorno = data?.configs?.retorno;
  const auto = data?.configs?.automationRetorno;
  if (retorno && setters.setCodstatusRetornoEmAberto) {
    setters.setCodstatusRetornoEmAberto(String(retorno.codstatus || '').trim());
    const extra = Array.isArray(retorno.extraStatuses)
      ? retorno.extraStatuses
          .filter((v) => v != null && v !== '')
          .map((v) => String(v).trim())
          .filter((v) => v !== '')
      : [];
    if (setters.setExtraRetornoStatusesEmAberto) {
      setters.setExtraRetornoStatusesEmAberto(extra);
    }
  }
  if (auto && setters.setCodstatusPedidoVinculadoDisponivel) {
    setters.setCodstatusPedidoVinculadoDisponivel(String(auto.statusPedidoDisponivel || '').trim());
  }
}

export function applyBoardBootstrapBell(data, setFuturaRetornoBellApi) {
  if (typeof setFuturaRetornoBellApi !== 'function') return;
  const bell = data?.bell;
  if (!bell) {
    setFuturaRetornoBellApi({ rows: [], mode: null, loaded: false });
    return;
  }
  const rows = (bell.rows || []).map((r) => ({
    technician: String(r.technician || '').trim(),
    pendente: Number(r.pendente) || 0,
    liberado: Number(r.liberado) || 0,
  }));
  const mode = bell.mode === 'liberado' || bell.mode === 'pendente' ? bell.mode : null;
  setFuturaRetornoBellApi({ rows, mode, loaded: true });
}

export function applyBoardBootstrapTeam(data, setTeamData) {
  if (typeof setTeamData !== 'function' || !data?.team) return null;
  const assembled = assembleBoardTeamData(data.team);
  setTeamData(assembled);
  return assembled;
}

export { assembleBoardTeamData };
